// CTSK-310 — row-level provenance badge for internal-registry DSR rows.
//
// The `installed_by` stamp records HOW an internal-registry entry got
// into the data-sources file: `manifest-install:<toolId>` (CTSK-292
// manifest install), `bundle:<bundleUid>` (CTSK-303 bundle import;
// CTSK-306 will add more of these), or a hand/backfill stamp
// (`manual-backfill-<env>-<date>`, CTSK-307's `backfill-http-<date>`,
// or anything an operator typed). Pre-CTSK-310 that stamp was only
// visible in the expanded detail panel; the badge compresses it to a
// single letter + short class label so provenance reads at row-glance.
// The full stamp stays on hover (title attribute) and in the detail
// panel.

export type ProvenanceClass = 'manifest' | 'bundle' | 'hand';

export interface ProvenanceInfo {
  letter: 'M' | 'B' | 'H';
  label: string;
  cls: ProvenanceClass;
}

/** Classify an `installed_by` stamp into its provenance family.
 *  Anything that isn't a manifest-install or bundle stamp is
 *  hand-class (H) — that bucket covers `manual-backfill-*`,
 *  CTSK-307's `backfill-http-*`, and free-text operator stamps; the
 *  label distinguishes backfills from true hand-adds. */
export function classifyInstalledBy(stamp: string): ProvenanceInfo {
  if (stamp.startsWith('manifest-install:')) return { letter: 'M', label: 'manifest', cls: 'manifest' };
  if (stamp.startsWith('bundle:')) return { letter: 'B', label: 'bundle', cls: 'bundle' };
  return { letter: 'H', label: stamp.includes('backfill') ? 'backfill' : 'hand-added', cls: 'hand' };
}

const CLASS_COLORS: Record<ProvenanceClass, string> = {
  manifest: 'var(--info-primary)',
  bundle: 'var(--accent-primary)',
  hand: 'var(--warning-primary)',
};

export function InstalledByBadge({ stamp }: { stamp?: string | null }) {
  // Schema requires installed_by on internal-registry entries, but a
  // hand-edited file can drop it — degrade to a hoverable question
  // mark rather than an em-dash (the dash reads as "not applicable",
  // which is wrong here: it applies, it's just missing).
  if (!stamp) {
    return (
      <span style={{ color: 'var(--text-muted)', fontSize: 11 }} title="No installed_by stamp on this entry">
        ?
      </span>
    );
  }
  const { letter, label, cls } = classifyInstalledBy(stamp);
  const color = CLASS_COLORS[cls];
  return (
    <span title={stamp} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          borderRadius: 4,
          fontSize: 10,
          fontWeight: 700,
          background: `color-mix(in srgb, ${color} 18%, var(--surface-raised))`,
          color,
          border: `1px solid color-mix(in srgb, ${color} 40%, transparent)`,
        }}
      >
        {letter}
      </span>
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</span>
    </span>
  );
}
